import React, {useCallback} from "react";
import {formatDateTime, minutesToHhMm} from "@/utils/DateUtils";
import {DropdownButton, DropdownItem, DropdownItemGroup, DropdownItemGroups, Icon, useNotifications} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {TimeDiff} from "@widgets/employees/editor/timeEntries/columns/WorkedHoursSummaryFormatter";
import {ArrowLeftStartOnRectangleIcon, ArrowRightEndOnRectangleIcon, PauseIcon} from "@heroicons/react/20/solid";
import {CheckIcon} from "@heroicons/react/16/solid";
import {SetStateFn} from "@/utils/SetStateUtils";
import {EmployeeInReport} from "@/data/entities/employee";
import {useMutation} from "@tanstack/react-query";
import {notifyDanger, notifySuccess} from "@/utils/NotifyUtils";
import {api} from "@/data/api";

type CloseFn = (() => void);

type MutateProps = {
    newBreakMinutes: number,
    closeDropdown: CloseFn,
};

type Props = {
    timeDiff: TimeDiff,
    employee: EmployeeInReport,
    setData: SetStateFn<EmployeeInReport[]>,
};

const TimeEntryInReport = ({timeDiff, employee, setData}: Props) => {
    const intl = useIntl();
    const {notify} = useNotifications();
    const {hours, minutes} = minutesToHhMm(timeDiff.workedMinutes);
    const {mutate, isPending} = useMutation({
        mutationFn: async (mutateProps: MutateProps) => {
            const result = await api.timeEntry.updateTimeEntry(timeDiff.timeEntry.id, {
                clockIn: timeDiff.timeEntry.clockIn,
                clockOut: timeDiff.timeEntry.clockOut,
                breakMinutes: mutateProps.newBreakMinutes,
            })
            return {
                result,
                closeDropdown: mutateProps.closeDropdown,
            };
        },
        onSuccess: ({result, closeDropdown}) => {
            notifySuccess(intl.formatMessage({id: "saved"}), notify)
            closeDropdown();

            // We are updating the state, so we don't have to re-fetch the
            // entire timesheet to view the changes.
            setData(prevData => prevData.map((e: EmployeeInReport) => e.id !== employee.id ? e : ({
                ...e,
                timeEntries: e.timeEntries.map(t => t.id !== timeDiff.timeEntry.id ? t : result)
            })));
        },
        onError: () => {
            notifyDanger(intl.formatMessage({id: "systemError"}), notify);
        },
    });
    const handleE = useCallback((newBreakMinutes: number) => (e: React.MouseEvent, closeDropdown: CloseFn) => {
        e.preventDefault();
        mutate({newBreakMinutes, closeDropdown});
    }, [mutate]);

    return (
        <DropdownButton
            color={"secondary"}
            size={"xs"}
            items={
                <DropdownItemGroups>
                    <DropdownItemGroup>
                        <DropdownItem buttonAction={{as: "groupLabel", actionProps: {}}}>
                            <FormattedMessage id={"clockedInAndOut"}/>
                        </DropdownItem>
                        <DropdownItem
                            buttonAction={{as: "button", actionProps: {}}}
                            disabled={true}
                            icon={<Icon icon={ArrowRightEndOnRectangleIcon}/>}
                        >
                            {formatDateTime(timeDiff.timeEntry.clockIn)}
                        </DropdownItem>
                        <DropdownItem
                            buttonAction={{as: "button", actionProps: {}}}
                            disabled={true}
                            icon={<Icon icon={ArrowLeftStartOnRectangleIcon}/>}
                        >
                            {formatDateTime(timeDiff.timeEntry.clockOut || "")}
                        </DropdownItem>
                        <DropdownItem
                            buttonAction={{as: "button", actionProps: {}}}
                            disabled={true}
                            icon={<Icon icon={PauseIcon}/>}
                        >
                            <FormattedMessage id={"nMinutes"} values={{n: timeDiff.timeEntry.breakMinutes}}/>
                        </DropdownItem>
                    </DropdownItemGroup>
                    <DropdownItemGroup>
                        <DropdownItem buttonAction={{as: "groupLabel", actionProps: {}}}>
                            <FormattedMessage id={"paused"}/>
                        </DropdownItem>

                        <DropdownItem
                            buttonAction={{
                                as: "button",
                                actionProps: {
                                    onClick: handleE(0),
                                }
                            }}
                            disabled={timeDiff.timeEntry.breakMinutes === 0 || isPending}
                            loading={isPending}
                            icon={timeDiff.timeEntry.breakMinutes !== 0 ? "empty" : <Icon icon={CheckIcon}/>}
                        >
                            <FormattedMessage id={"nMinutes"} values={{n: 0}}/>
                        </DropdownItem>

                        <DropdownItem
                            buttonAction={{
                                as: "button",
                                actionProps: {
                                    onClick: handleE(15),
                                }
                            }}
                            disabled={timeDiff.timeEntry.breakMinutes === 15 || isPending}
                            loading={isPending}
                            icon={timeDiff.timeEntry.breakMinutes !== 15 ? "empty" : <Icon icon={CheckIcon}/>}
                        >
                            <FormattedMessage id={"nMinutes"} values={{n: 15}}/>
                        </DropdownItem>

                        <DropdownItem
                            buttonAction={{
                                as: "button",
                                actionProps: {
                                    onClick: handleE(30),
                                }
                            }}
                            disabled={timeDiff.timeEntry.breakMinutes === 30 || isPending}
                            loading={isPending}
                            icon={timeDiff.timeEntry.breakMinutes !== 30 ? "empty" : <Icon icon={CheckIcon}/>}
                        >
                            <FormattedMessage id={"nMinutes"} values={{n: 30}}/>
                        </DropdownItem>

                    </DropdownItemGroup>
                </DropdownItemGroups>
            }
        >
            <FormattedMessage
                id={"formattedHoursMinutes"}
                values={{hours, minutes}}
            />
        </DropdownButton>
    )
};

export default TimeEntryInReport;